import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { tenantsService, TenantProfileUpdate } from '@/services/tenants';
import { useAuthStore } from '@/context/authStore';
import { Tenant } from '@/types';
import toast from 'react-hot-toast';

export function useTenantProfile() {
  return useQuery({
    queryKey: ['tenant-profile'],
    queryFn: tenantsService.getProfile,
  });
}

/**
 * Red de seguridad multi-tenant: mantiene el tenant del store sincronizado con
 * el usuario logueado. Si el tenant guardado (localStorage) es de OTRA
 * veterinaria (id distinto al del usuario) o falta, lo limpia y re-fetchea.
 * Cubre el caso de recargar la página con un tenant viejo persistido.
 */
export function useEnsureTenant() {
  const user = useAuthStore((s) => s.user);
  const tenant = useAuthStore((s) => s.tenant);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const isImpersonating = useAuthStore((s) => s.impersonation.isImpersonating);
  const setTenant = useAuthStore((s) => s.setTenant);

  // En impersonación el tenant no es el del usuario superadmin, no validar id.
  const mismatched =
    !!user && user.role !== 'superadmin' && !isImpersonating &&
    !!tenant && tenant.id !== user.tenant_id;

  // Limpiar de inmediato si es de otra veterinaria (evita mostrar datos ajenos).
  useEffect(() => {
    if (mismatched) setTenant(null);
  }, [mismatched, setTenant]);

  // Traer SIEMPRE la config de la veterinaria (no solo si falta): así los flags
  // que prende el admin/superadmin (precios por medio de pago, fraccionados, etc.)
  // se reflejan al recargar, sin obligar al usuario a cerrar sesión.
  // Durante impersonación también se trae (es el perfil de la vete impersonada).
  const shouldFetch =
    isAuthenticated && !!user && (user.role !== 'superadmin' || isImpersonating);

  const { data } = useQuery({
    queryKey: ['tenant-profile'],
    queryFn: tenantsService.getProfile,
    enabled: shouldFetch,
    refetchOnWindowFocus: true,
    staleTime: 30_000,
  });

  useEffect(() => {
    if (data) setTenant(data);
  }, [data, setTenant]);
}

export function useUpdateTenantProfile() {
  const queryClient = useQueryClient();
  const setTenant = useAuthStore((state) => state.setTenant);

  return useMutation({
    mutationFn: (data: TenantProfileUpdate) => tenantsService.updateProfile(data),
    onSuccess: (updatedTenant: Tenant) => {
      // Actualizar el query cache
      queryClient.invalidateQueries({ queryKey: ['tenant-profile'] });
      // Actualizar el store global para que el sidebar se actualice
      setTenant(updatedTenant);
      toast.success('Perfil actualizado correctamente');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Error al actualizar el perfil');
    },
  });
}

export function useUploadTenantLogo() {
  const queryClient = useQueryClient();
  const setTenant = useAuthStore((state) => state.setTenant);

  return useMutation({
    mutationFn: (file: File) => tenantsService.uploadLogo(file),
    onSuccess: (updatedTenant: Tenant) => {
      // Actualizar el query cache
      queryClient.invalidateQueries({ queryKey: ['tenant-profile'] });
      // Actualizar el store global para que el sidebar se actualice
      setTenant(updatedTenant);
      toast.success('Logo actualizado correctamente');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Error al subir el logo');
    },
  });
}

export function useDeleteTenantLogo() {
  const queryClient = useQueryClient();
  const setTenant = useAuthStore((state) => state.setTenant);

  return useMutation({
    mutationFn: tenantsService.deleteLogo,
    onSuccess: (updatedTenant: Tenant) => {
      // Actualizar el query cache
      queryClient.invalidateQueries({ queryKey: ['tenant-profile'] });
      // Actualizar el store global para que el sidebar se actualice
      setTenant(updatedTenant);
      toast.success('Logo eliminado');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Error al eliminar el logo');
    },
  });
}
